/**
 * One quotation line (prototype `vQuote`'s `rowHtml`).
 *
 * Selling price is editable, committing on blur so a request isn't fired per keystroke.
 * Violations are flagged live from `lib/pricing.ts` — the same rules the server re-runs on
 * send — and the offending input is outlined so the problem is where the fix is.
 *
 * Prices are displayed in the customer's chosen currency but stored in base, so the input
 * converts on the way in and out.
 *
 * Qty is READ-ONLY on `/quote` (Task 11): `quotation.update_quotation_line`'s wire only accepts
 * `{margin, sell_unit}` — there is no field to change a line's quantity through this API, unlike
 * the old `/api/v1` PATCH. The "sell UOM" preset picker is gone for the same reason (no
 * `sell_uom` field either); `item.sell_uoms` is no longer read here.
 *
 * On `/revisions` (`structural` present, Task 6), qty and remove ARE editable — but they don't
 * PATCH anything. `apply_revisions` is the only endpoint that can change either, and it bumps the
 * revision on every call, so both queue into the caller's batch (`lib/revisionBatch.ts`) and wait
 * for one explicit "Apply" instead.
 */

import { useEffect, useState } from 'react'
import { LineTextarea } from '../../components/LineTextarea'
import { Badge } from '../../components/Badge'
import { Money } from '../../components/Money'
import { Button } from '../../components/ui'
import { convert } from '../../lib/fx'
import { checkLine } from '../../lib/pricing'
import type { WireFx } from '../../lib/api/wire'
import type {
  Currency,
  Item,
  Money as MoneyType,
  PricingRulesSettings,
  QuotationLine,
  RuleViolation,
} from '../../lib/types'

/**
 * A row's own slice of `StructuralEditing` (`QuotationEditor.tsx`), already keyed to this line.
 *
 * `op` excludes `'add'` on purpose: an existing quotation line can only be resized or removed
 * here, never (re-)added — there is no add control in this row, so a pending `'add'` for this
 * item_code (queued elsewhere, from a revision request for a NEW line) is not this row's state
 * to show. `QuoteLinesTable` narrows it out before it reaches here.
 */
export interface RowStructuralEditing {
  pending: { op: 'set_qty' | 'remove'; qty: number } | undefined
  onQueueQty: (qty: number) => void
  onQueueRemove: () => void
  onDiscard: () => void
}

export function QuoteRow({
  line,
  locked,
  onToggleLock,
  item,
  violations,
  rules,
  displayCurrency,
  fx,
  categoryMarginPct,
  busy,
  onChangeSellUnit,
  onChangeText,
  structural,
}: {
  line: QuotationLine
  /** Price locked against a bulk markup change. Session-only — no field on the doctype (`D24`). */
  locked: boolean
  onToggleLock: () => void
  item: Item | undefined
  /** Server-side violations; used until the pricing rules have loaded. */
  violations: RuleViolation[]
  rules: PricingRulesSettings | undefined
  displayCurrency: Currency
  fx: WireFx
  categoryMarginPct: number | undefined
  busy: boolean
  onChangeSellUnit: (sell: MoneyType) => void
  onChangeText: (patch: { description?: string; offer_remarks?: string }) => void
  /** Absent on `/quote` — qty stays the plain read-only number it always was. */
  structural?: RowStructuralEditing
}) {
  /*
    The two printed texts, committed on blur (Akhil, item 8). Drafted locally so a keystroke is
    not a PATCH, and reset whenever the saved value changes underneath.
  */
  const [descDraft, setDescDraft] = useState(line.description)
  useEffect(() => setDescDraft(line.description), [line.description])
  const [remarkDraft, setRemarkDraft] = useState(line.offer_remarks)
  useEffect(() => setRemarkDraft(line.offer_remarks), [line.offer_remarks])
  /*
    The PO side, READ-ONLY and ALWAYS shown (Akhil, 2026-09-15): the supplier's own figure in
    the supplier's currency — "the base price". The cost above is that figure restated into the
    quotation's currency, and an exchange change moves the cost while this stays, which is
    exactly why it has to be visible beside it. Currency is decided on the COMPARISON page; this
    only reports what was decided there.
  */
  const poConverted = line.po_currency !== line.cost_unit.currency || Math.abs(line.conversion_rate - 1) > 1e-9
  const inDisplay = (value: MoneyType) => convert(value, displayCurrency, fx)
  /** What the input shows for the saved price — the yardstick for "did the user change it?". */
  const savedAsShown = inDisplay(line.sell_unit).amount.toFixed(2)

  const [sellDraft, setSellDraft] = useState(savedAsShown)

  useEffect(() => setSellDraft(savedAsShown), [savedAsShown])

  /**
   * Live rule feedback on the DRAFT, not on the saved value — §3 wants the flag while the
   * purchaser types, not one round-trip later. Falls back to the parent's server-side
   * violations when no rules have loaded.
   */
  const draftSell: MoneyType = {
    amount: Number(sellDraft) || 0,
    currency: displayCurrency,
  }
  /*
    An UNTOUCHED price is checked exactly as stored. Only a typed one goes through the display
    currency — the display value is rounded to the cent, and converting it back (USD 1.84 →
    AED 6.716 against a cost of 6.72) flagged "below cost" on a line nobody had edited whenever
    the view was in another currency (ENQ-2026-00146, 2026-09-16).
  */
  const liveViolations = rules
    ? checkLine(
        sellDraft === savedAsShown ? line : { ...line, sell_unit: convert(draftSell, line.sell_unit.currency, fx) },
        rules,
      )
    : violations

  const hasViolation = liveViolations.length > 0

  /*
    Queued structural state for THIS line. `pending` never PATCHes anything — see the file
    header — it only decides what the qty cell and the actions cell show until the caller's
    batch is applied.
  */
  const pending = structural?.pending
  const markedForRemoval = pending?.op === 'remove'

  /**
   * The QUEUED quantity, not the saved one, when there is one (fix round, final review: I6).
   * Before this, the qty box showed the pending value while the Line column kept multiplying the
   * OLD `line.qty` — qty 3→10 on a 100/u line showed box 10, Line 300, and the totals panel (which
   * already read the queued qty via `previewLines`) showed 1000. Three numbers for one line. A
   * queued removal does not change this — its Line figure is informational only; the SUM this row
   * feeds (`QuoteLinesTable`'s `subtotalOf`) excludes it entirely, and the strike-through below
   * says so.
   */
  const effectiveQty = pending?.op === 'set_qty' ? pending.qty : line.qty
  const lineTotal: MoneyType = { amount: line.sell_unit.amount * effectiveQty, currency: line.sell_unit.currency }

  /** What the qty box shows: the queued value if this line has one, else the saved qty. */
  const savedQtyAsShown = String(pending?.op === 'set_qty' ? pending.qty : line.qty)
  const [qtyDraft, setQtyDraft] = useState(savedQtyAsShown)
  useEffect(() => setQtyDraft(savedQtyAsShown), [savedQtyAsShown])

  return (
    <tr style={{ ...(busy ? { opacity: 0.6 } : {}), ...(markedForRemoval ? { opacity: 0.5 } : {}) }}>
      <td>
        {line.cust_sno ? (
          <>
            <Badge tone="muted" title="Customer S.No">
              #{line.cust_sno}
            </Badge>{' '}
          </>
        ) : null}
        <span style={markedForRemoval ? { textDecoration: 'line-through' } : undefined}>
          {item?.name ?? line.item_code}
        </span>{' '}
        {line.tag === 'provision' ? <Badge tone="violet">prov</Badge> : <Badge tone="info">RFQ</Badge>}

        <div style={{ color: 'var(--muted)', fontSize: 11 }}>
          {line.source}
          {categoryMarginPct !== undefined ? ` · ${line.category} margin ${categoryMarginPct}%` : null}
        </div>

        {/* What the customer's copy prints under the name — editable here, on the document. */}
        {/* Labelled, not just placeholdered — two bare boxes read as the same field (Akhil, 2026-09-15). */}
        <div style={{ marginTop: 4, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 6px', alignItems: 'center' }}>
          <span style={{ fontSize: 10.5, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Description</span>
          <LineTextarea
            value={descDraft}
            placeholder={line.item_name ? `prints as: ${line.item_name}` : 'what the customer reads under the item'}
            aria-label={`Description for ${line.item_code}`}
            title="Printed description — the customer sees this under the item name. Enter for a new line."
            onChange={(e) => setDescDraft(e.target.value)}
            onBlur={() => {
              if (descDraft !== line.description) onChangeText({ description: descDraft })
            }}
          />
          <span style={{ fontSize: 10.5, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Remark</span>
          <LineTextarea
            value={remarkDraft}
            placeholder="ex-stock · lead time · subject to availability"
            aria-label={`Offer remark for ${line.item_code}`}
            title="Offer remark — printed against the line on the customer's copy. Enter for a new line."
            style={{ borderColor: 'var(--teal)', fontStyle: remarkDraft ? 'normal' : 'italic' }}
            onChange={(e) => setRemarkDraft(e.target.value)}
            onBlur={() => {
              if (remarkDraft !== line.offer_remarks) onChangeText({ offer_remarks: remarkDraft })
            }}
          />
        </div>

        {hasViolation ? <div className="warnflag">⚠ {liveViolations.map((v) => v.reason).join(' · ')}</div> : null}

        {pending ? (
          <div style={{ marginTop: 4 }}>
            <Badge tone="warn">{markedForRemoval ? 'Marked for removal (pending)' : `Qty → ${pending.qty} (pending)`}</Badge>
          </div>
        ) : null}
      </td>

      <td className="right mono">
        {structural ? (
          markedForRemoval ? (
            <span style={{ textDecoration: 'line-through' }}>{line.qty}</span>
          ) : (
            <input
              type="number"
              step="1"
              min="0"
              value={qtyDraft}
              aria-label={`Quantity for ${line.item_code}`}
              style={{ width: 60 }}
              onChange={(e) => setQtyDraft(e.target.value)}
              onBlur={() => {
                // Same reasoning as the sell-price box below: only queue on an actual change,
                // not on a bare focus/blur.
                if (qtyDraft !== savedQtyAsShown) {
                  structural.onQueueQty(Number(qtyDraft) || 0)
                }
              }}
            />
          )
        ) : (
          line.qty
        )}
      </td>

      {/* The unit in its OWN column (Akhil, 2026-10-28), not a tag under the quantity — "5" alone
          is not a quantity when the same item is sold by the piece, the bag and the carton. */}
      <td title="Unit this line is quoted per">{line.sell_uom || line.uom || '—'}</td>

      <td className="right mono">
        <b><Money value={line.cost_unit} in={displayCurrency} fx={fx} /></b>
        <div
          style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap' }}
          title={`Supplier's own figure (the base price): ${line.po_currency_rate.toFixed(2)} ${line.po_currency}${
            poConverted ? ` × ${line.conversion_rate.toFixed(4)} = ${line.cost_unit.amount.toFixed(2)} ${line.cost_unit.currency}` : ''
          }. Currency is set on the comparison page.`}
        >
          <span style={{ textTransform: 'uppercase', letterSpacing: 0.3, fontSize: 10 }}>base</span>{' '}
          <span className="mono">
            {line.po_currency_rate.toFixed(2)} {line.po_currency}
          </span>
          {poConverted ? <span> × {line.conversion_rate.toFixed(4)}</span> : null}
        </div>
      </td>

      {/*
        The price lock. Sits immediately before the price it protects, so the two read as one
        control rather than a checkbox at the far end of the row.

        Locking does NOT make the price read-only: a purchaser can still type on a locked line, and
        should be able to — the lock is against the BULK markup, which is the thing that overwrites
        prices nobody was looking at. Making it read-only would mean unlocking to make the very
        edit the lock exists to preserve.
      */}
      <td className="right">
        <input
          type="checkbox"
          checked={locked}
          disabled={busy}
          aria-label={`Lock the price for ${line.item_code} against bulk markup changes`}
          title={
            locked
              ? 'Locked — a markup applied to every line will skip this one. Click to unlock.'
              : 'Unlocked — a markup applied to every line will reprice this one.'
          }
          onChange={onToggleLock}
          style={{ cursor: busy ? 'not-allowed' : 'pointer' }}
        />
      </td>

      <td className="right">
        <input
          type="number"
          step="0.01"
          value={sellDraft}
          aria-label={`Selling price for ${line.item_code}`}
          style={{
            width: 80,
            ...(hasViolation ? { borderColor: 'var(--danger)', color: 'var(--danger)' } : {}),
          }}
          onChange={(e) => setSellDraft(e.target.value)}
          onBlur={() => {
            // Compare the DISPLAYED strings, not the typed float against the unrounded
            // saved value: AED 2.408 shows as USD 0.66, and comparing 0.66 to 0.6558…
            // fired a PATCH on a bare focus/blur, re-pricing the line every time.
            if (sellDraft !== savedAsShown) {
              onChangeSellUnit({ amount: Number(sellDraft) || 0, currency: displayCurrency })
            }
          }}
        />
      </td>

      <td className="right mono">
        <span style={markedForRemoval ? { textDecoration: 'line-through' } : undefined}>
          <Money value={lineTotal} in={displayCurrency} fx={fx} />
        </span>
      </td>

      {structural ? (
        <td className="right">
          {pending ? (
            <Button size="sm" onClick={structural.onDiscard}>
              Undo
            </Button>
          ) : (
            <Button size="sm" variant="danger" onClick={structural.onQueueRemove}>
              ✕ Remove
            </Button>
          )}
        </td>
      ) : null}
    </tr>
  )
}
